import { ActionPalette, GrayscalePalette } from '@tcl-boron-colors/colors';
import { StatusVariant, PRIMARY_STATUS_COLORS, SECONDARY_STATUS_COLORS } from '@tcl-argon-utils/utils';

export interface DropdownStylesData {
  isDisabled: boolean;
  isPrimary: boolean;
  status: StatusVariant;
  isEmpty: boolean;
}

export const defaultDownshiftDropdownOverrideClasses = {
  clearButton: '',
  highlightedMenuItem: '',
  invisibleMenu: '',
  menu: '',
  menuItem: '',
  placeholder: '',
  inputWrapper: '',
  selectedMenuItem: '',
  value: '',
  valueContainer: '',
};

export const inputWrapper = {
  position: 'relative',
};

const getValueContainerBorderColor: (status: StatusVariant, isPrimary: boolean) => string = (status, isPrimary) => {
  const isDefault = status === 'default';
  if (isPrimary) {
    return isDefault ? 'transparent' : PRIMARY_STATUS_COLORS[status];
  }
  return PRIMARY_STATUS_COLORS[status];
};

export const valueContainer = {
  '&:focus, &[aria-expanded="true"]': {
    backgroundColor: '',
    borderColor: ActionPalette[500],
  },
  '&:hover': {
    borderColor: ActionPalette[500],
  },
  transition: 'border-color 150ms ease-out',
  alignItems: 'center',
  backgroundColor: ({ status }: DropdownStylesData) => SECONDARY_STATUS_COLORS[status],
  border: '1px solid',
  borderColor: ({ status, isPrimary }: DropdownStylesData) => getValueContainerBorderColor(status, isPrimary),
  borderRadius: ({ isPrimary }: DropdownStylesData) => (isPrimary ? '20px' : '3px'),
  boxSizing: 'border-box',
  boxShadow: ({ isPrimary }: DropdownStylesData) => (isPrimary ? `0px 1px 3px 1px ${GrayscalePalette[300]}` : ''),
  cursor: 'pointer',
  display: 'flex',
  height: '40px',
  justifyContent: 'space-between',
  // This is OK because we outline the valueContainer with a colored border
  outline: 0,
  padding: '12px 10px 12px 20px',
  color: ActionPalette[600],
};

export const value = {
  fontSize: ({ isEmpty }: DropdownStylesData) => (!isEmpty ? '12px' : '14px'),
  flex: '1 1 auto',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  fontWeight: 400,
};

export const placeholder = {
  color: ({ isDisabled }: DropdownStylesData) => (isDisabled ? ActionPalette[400] : ActionPalette[500]),
};

export const menu = {
  backgroundColor: 'white',
  border: '1px solid',
  borderColor: ActionPalette[400],
  borderRadius: '2px',
  listStyle: 'none',
  margin: 0,
  maxHeight: '500px',
  outline: 0,
  overflow: 'auto',
  position: 'absolute',
  top: 'calc(100% + 5px)',
  width: '100%',
  zIndex: 1,
  padding: '8px 0px',
};

export const invisibleMenu = {
  visibility: 'hidden',
  pointerEvents: 'none',
};

export const menuItem = {
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  fontSize: '14px',
  lineHeight: '16px',
  height: '34px',
  padding: '0px 15px',
  color: ActionPalette[600],
  transition: 'background-color 150ms ease-out, color 150ms ease-out',
};

export const selectedMenuItem = {
  display: 'flex',
  alignItems: 'center',
  fontWeight: 700,
  background: ActionPalette[400],
};

export const highlightedMenuItem = {
  backgroundColor: ActionPalette[200],
};

export const clearButton = {
  display: 'flex',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  borderRight: `1px solid ${ActionPalette[400]}`,
  cursor: 'inherit',
  height: '15px',
  margin: '0px 8px 0px',
  padding: '0px 10px 0px',
};

export const disabled = {
  opacity: '0.5',
  pointerEvents: 'none',
};
